import React, { useState } from "react";
import CommonModal from "../../../../common/commonmodal/CommonModal";
import CustomSelect from "../../../../common/formfields/CustomSelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBuilding,
    faCreditCard,
    faUserTie,
    faWheelchair,
    faShieldHalved,
    faBolt,
    faBan
} from "@fortawesome/free-solid-svg-icons";

const AllocateParkingModal = ({ isOpen, onClose, onConfirm, selectedSlots = [], allocationTypeOptions = [], wingOptions = [], unitOptions = [] }) => {
    const [allocationType, setAllocationType] = useState("");
    const [wingId, setWingId] = useState("");
    const [unitId, setUnitId] = useState("");

    // Automatically set the first allocationType as selected when modal opens
    React.useEffect(() => {
        if (isOpen) {
            setWingId("");
            setUnitId("");
            if (allocationTypeOptions.length > 0) {
                setAllocationType(allocationTypeOptions[0].value);
            }
        }
    }, [isOpen, allocationTypeOptions]);

    const handleConfirm = () => {
        onConfirm({
            allocationTypeId: allocationType?.value || allocationType,
            allocatedUnitId: unitId?.value || unitId || 0,
            slots: selectedSlots
        });
        onClose();
    };

    const selectedWingId = wingId?.value || wingId;
    const filteredUnitOptions = selectedWingId
        ? unitOptions.filter(u => (u.original.WingId || u.original.wingId) == selectedWingId)
        : unitOptions;

    const modalFooter = (
        <div className="flex justify-end gap-3">
            <button
                onClick={onClose}
                className="px-5 py-2 text-sm font-bold text-gray-600 dark:text-textsecondary bg-gray-100 dark:bg-actionitembg rounded-lg hover:bg-gray-200 dark:hover:opacity-85 transition-all"
            >
                Cancel
            </button>
            <button
                onClick={handleConfirm}
                disabled={selectedSlots.length === 0 || !allocationType}
                className="px-5 py-2 text-sm font-bold text-white rounded-lg transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center"
                style={{ backgroundColor: "var(--color-primaryaccent)" }}
            >
                Confirm Allocation
            </button>
        </div>
    );

    return (
        <CommonModal
            isOpen={isOpen}
            onClose={onClose}
            title="Allocate Selected Parking Bays"
            width="md:w-[600px]"
            footerContent={modalFooter}
        >
            <div className="flex flex-col gap-4 pt-1">
                {/* Selected Inventory */}
                <div>
                    <label className="block text-[11px] font-bold text-gray-800 dark:text-textprimary mb-1.5">
                        Selected Inventory ({selectedSlots.length} Bays)
                    </label>
                    <div className="flex flex-wrap gap-1.5 p-2 bg-gray-50 dark:bg-dashboardbg border border-gray-100 dark:border-bordercolor rounded-lg">
                        {selectedSlots.map((slot) => (
                            <span 
                                key={slot.id} 
                                className="px-2 py-0.5 bg-blue-50 dark:bg-actionitembg text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900 rounded-[6px] text-[10px] font-semibold tracking-wide"
                            >
                                {slot.slotNumber || slot.id}
                            </span>
                        ))}
                        {selectedSlots.length === 0 && (
                            <span className="text-xs text-gray-400">No bays selected</span>
                        )}
                    </div>
                </div>

                {/* Select Allocation Type */}
                <div>
                    <label className="block text-xs font-bold text-gray-800 dark:text-textprimary mb-2">
                        Select Allocation Type
                    </label>
                    <div className="grid grid-cols-2 gap-2.5">
                        {allocationTypeOptions.map((type) => {
                            const isSelected = allocationType === type.value;
                            return (
                                <button
                                    key={type.value}
                                    onClick={() => setAllocationType(type.value)}
                                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border-2 transition-all duration-200 text-left
                                        ${isSelected
                                            ? "border-red-500 bg-red-50/10 dark:bg-red-500/10"
                                            : "border-gray-200 dark:border-bordercolor hover:border-red-300 dark:hover:border-red-500/50 bg-white dark:bg-actionitembg"
                                        }`}
                                >
                                    <FontAwesomeIcon
                                        icon={faBuilding}
                                        className={`text-sm ${isSelected ? "text-red-500" : "text-gray-400 dark:text-textsecondary"}`}
                                    />
                                    <span className={`text-[11px] font-semibold ${isSelected ? "text-red-600 dark:text-red-400" : "text-gray-700 dark:text-textprimary"}`}>
                                        {type.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Dynamic Content based on Allocation Type */}
                <div className="bg-gray-50 dark:bg-dashboardbg p-4 rounded-xl border border-gray-100 dark:border-bordercolor">
                    <div className="flex flex-col gap-3">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[11px] font-bold text-gray-600 dark:text-textprimary mb-1.5">
                                    Target Wing
                                </label>
                                <CustomSelect
                                    options={wingOptions}
                                    value={wingId}
                                    onChange={(val) => {
                                        setWingId(val);
                                        setUnitId("");
                                    }}
                                    placeholder="Select a wing..."
                                />
                            </div>
                            <div>
                                <label className="block text-[11px] font-bold text-gray-600 dark:text-textprimary mb-1.5">
                                    Target Unit
                                </label>
                                <CustomSelect
                                    options={filteredUnitOptions}
                                    value={unitId}
                                    onChange={setUnitId}
                                    placeholder="Select a unit..."
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </CommonModal>
    );
};

export default AllocateParkingModal;
